import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ApiConnectionString } from "../../../constants/ApiConntection";

export default function AddQualification({ user, onHasQualificationChange, error }) {
  const [qualifications, setQualifications] = useState([]);
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState("");
  const [editingId, setEditingId] = useState(null);

  const updateTimersRef = useRef({});

  const getAuthConfig = () => {
    return user?.token ? { headers: { Authorization: `Bearer ${user.token}` } } : undefined;
  };

  const getAllQualifications = async () => {
    setLocalError("");
    setIsLoading(true);
    try {
      const res = await axios.get(`${ApiConnectionString}/api/qualifications`, getAuthConfig());
      setQualifications(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setLocalError("Could not load qualifications");
    } finally {
      setIsLoading(false);
    }
  };

  const createQualification = async () => {
    if (!name.trim()) return;

    setLocalError("");
    setIsLoading(true);
    try {
      await axios.post(`${ApiConnectionString}/api/qualification`, { name: name.trim() }, getAuthConfig());

      setName("");
      await getAllQualifications();
    } catch (e) {
      setLocalError("Could not create qualification");
    } finally {
      setIsLoading(false);
    }
  };

  const updateQualification = async (qualification) => {
    if (!qualification?.id) return;

    setLocalError("");
    try {
      await axios.put(`${ApiConnectionString}/api/qualification`, { id: qualification.id, name: qualification.name }, getAuthConfig());
    } catch (e) {
      setLocalError("Could not update qualification");
    }
  };

  const scheduleQualificationUpdate = (id, nextName) => {
    if (!id) return;

    if (updateTimersRef.current[id]) clearTimeout(updateTimersRef.current[id]);

    updateTimersRef.current[id] = setTimeout(() => {
      updateQualification({ id, name: nextName });
      updateTimersRef.current[id] = null;
    }, 500);
  };

  const flushQualificationUpdate = (id) => {
    if (!id) return;

    if (updateTimersRef.current[id]) {
      clearTimeout(updateTimersRef.current[id]);
      updateTimersRef.current[id] = null;
    }

    const q = qualifications.find((x) => x.id === id);
    if (q) updateQualification({ id: q.id, name: q.name || "" });
  };

  const deleteQualification = async (id) => {
    setLocalError("");
    setIsLoading(true);
    try {
      await axios.delete(`${ApiConnectionString}/api/qualification/${id}`, getAuthConfig());
      setQualifications((prev) => prev.filter((x) => x.id !== id));
      if (editingId === id) setEditingId(null);
    } catch (e) {
      setLocalError("Could not delete qualification");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAllQualifications();
  }, []);

  useEffect(() => {
    if (onHasQualificationChange) onHasQualificationChange(qualifications.length > 0);
  }, [qualifications.length]);

  useEffect(() => {
    return () => {
      Object.values(updateTimersRef.current).forEach((t) => {
        if (t) clearTimeout(t);
      });
    };
  }, []);

  return (
    <div className="ppfShell w-100">
      <div className="ppfCard">
        <div className="ppfHead">
          <div className="ppfHeadLeft">
            <h3 className="ppfTitle mb-0">Qualifications</h3>
          </div>

          <button className="btn ppfBtnOutline btn-sm" onClick={getAllQualifications} disabled={isLoading} type="button">
            Refresh
          </button>
        </div>

        <div className="ppfBody">
          <div className="mb-3">
            <label className="form-label ppfLabel mb-1">Add qualification</label>

            <div className="input-group">
              <input
                className="form-control form-control-sm ppfInput"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Level 3 PT"
                disabled={isLoading}
              />
              <button className="btn ppfBtnPrimary btn-sm" onClick={createQualification} disabled={isLoading || !name.trim()} type="button">
                Add
              </button>
            </div>
          </div>

          {localError ? <div className="alert alert-danger pp-upload-alert mb-3">{localError}</div> : null}

          {error ? <div className="invalid-feedback d-block mb-3">{error}</div> : null}

          {isLoading && qualifications.length === 0 ? (
            <div className="ppfHelpText">Loading...</div>
          ) : qualifications.length === 0 ? (
            <div className="ppfHelpText">No qualifications yet</div>
          ) : (
            <div className="list-group">
              {qualifications.map((q) => {
                const isEditing = editingId === q.id;

                return (
                  <div key={q.id} className="list-group-item py-2">
                    <div className="d-flex align-items-center gap-2">
                      <input
                        className="form-control form-control-sm ppfInput"
                        value={q.name || ""}
                        disabled={isLoading || !isEditing}
                        onChange={(e) => {
                          const next = e.target.value;
                          setQualifications((prev) => prev.map((x) => (x.id === q.id ? { ...x, name: next } : x)));
                          scheduleQualificationUpdate(q.id, next);
                        }}
                        onBlur={() => {
                          if (isEditing) flushQualificationUpdate(q.id);
                        }}
                      />

                      {!isEditing ? (
                        <button className="btn ppfBtnOutline btn-sm" type="button" onClick={() => setEditingId(q.id)} disabled={isLoading}>
                          Edit
                        </button>
                      ) : (
                        <button
                          className="btn ppfBtnPrimary btn-sm"
                          type="button"
                          onClick={() => {
                            flushQualificationUpdate(q.id);
                            setEditingId(null);
                          }}
                          disabled={isLoading}
                        >
                          Done
                        </button>
                      )}

                      <button className="btn btn-outline-danger btn-sm" type="button" onClick={() => deleteQualification(q.id)} disabled={isLoading}>
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="d-flex justify-content-between mt-3">
            <span className="ppfHelpText mb-0">Edits auto save while editing</span>
            <span className="ppfStepPill">{qualifications.length} total</span>
          </div>
        </div>
      </div>
    </div>
  );
}
